import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  buttonVariants,
} from "@simplicity/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

// Module-level pushState interception, patched once at load and shared by every hook instance via
// the refs below. React Router's client-side navigation all routes through
// `window.history.pushState`, so intercepting here lets the active guard show its dialog before the
// URL changes. The original is stashed on the history object itself rather than a module-local, so
// HMR re-evaluating this module cannot double-wrap and concurrent loaders share one pushState. The
// private props are modelled via an extended interface, not `as any`.

interface ExtendedHistory extends History {
  __nativePushState?: typeof History.prototype.pushState;
  __guardPatched?: boolean;
}

const extendedHistory = window.history as ExtendedHistory;

const nativePushState: typeof History.prototype.pushState = (extendedHistory.__nativePushState ??=
  window.history.pushState.bind(window.history));

// Shared refs that the active guard instance writes to.
let activeIsDirty: (() => boolean) | null = null;
let activeOnBlock: ((url: string, state: unknown) => void) | null = null;

if (!extendedHistory.__guardPatched) {
  extendedHistory.__guardPatched = true;

  window.history.pushState = function (...args: Parameters<typeof History.prototype.pushState>) {
    if (!activeIsDirty || !activeIsDirty() || !activeOnBlock) {
      nativePushState(...args);
      return;
    }
    const url = typeof args[2] === "string" ? args[2] : window.location.href;
    activeOnBlock(url, args[0]);
  };
}

/**
 * Guards against losing unsaved form changes. Intercepts `history.pushState` to catch all
 * client-side navigation (<Link>, <NavLink>, `navigate()`, onClick-driven `navigate()`), and uses
 * `beforeunload` for refresh / tab close. Back/forward interception is not supported under
 * <BrowserRouter>.
 */
export function useUnsavedChangesGuard(isDirty: boolean) {
  const [showDialog, setShowDialog] = useState(false);
  const pendingNavRef = useRef<{ url: string; state: unknown } | null>(null);
  const isDirtyRef = useRef(isDirty);
  const leavingRef = useRef(false);
  const navigate = useNavigate();

  // Sync isDirty into ref so pushState interceptor sees latest value
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Register this instance as the active guard
  useEffect(() => {
    activeIsDirty = () => isDirtyRef.current && !leavingRef.current;
    activeOnBlock = (url: string, state: unknown) => {
      pendingNavRef.current = { url, state };
      setShowDialog(true);
    };
    return () => {
      activeIsDirty = null;
      activeOnBlock = null;
    };
  }, []);

  // Handle browser refresh / tab close
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      if (leavingRef.current) return;
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleStay = useCallback(() => {
    setShowDialog(false);
    pendingNavRef.current = null;
  }, []);

  const handleLeave = useCallback(() => {
    setShowDialog(false);
    const pending = pendingNavRef.current;
    pendingNavRef.current = null;
    if (!pending) return;

    leavingRef.current = true;
    isDirtyRef.current = false;

    const basename = "/web-admin";
    let path = pending.url;
    if (path.startsWith(basename)) {
      path = path.slice(basename.length) || "/";
    }
    void navigate(path);

    requestAnimationFrame(() => {
      leavingRef.current = false;
    });
  }, [navigate]);

  const dialog = showDialog && (
    <AlertDialog open onOpenChange={handleStay}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes that will be lost. Are you sure you want to leave this page?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleStay}>Stay</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: "destructive" })}
            onClick={handleLeave}
          >
            Leave
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { dialog };
}
